import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import { TRIBES, SYMPTOM_OPTIONS } from "@/lib/tribes";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowLeft, Flame, Sparkles, Flag, ArrowRight } from "lucide-react";

const SYMPTOM_RATING_COLOR = { better: "#4A7C59", same: "#C9A96E", worse: "#B45E5E" };
const SYMPTOM_RATING_LABEL = { better: "Better", same: "Same", worse: "Worse" };
const ROADBLOCK_LABEL = {
  tongue_tie: "Tongue Tie",
  lip_tie: "Lip Tie",
  palate_width: "Palate Width",
  tonsils_adenoids: "Tonsils / Adenoids",
};
const ROADBLOCK_STATUS_STYLE = {
  none: { bg: "bg-cream", ink: "text-ink-mid", label: "No concern" },
  flagged: { bg: "bg-mauve-pale", ink: "text-mauve", label: "Flagged" },
  referred: { bg: "bg-gold/20", ink: "text-navy", label: "Referred" },
  cleared: { bg: "bg-emerald-50", ink: "text-emerald-700", label: "Cleared" },
};

function MiniChart({ data, dataKey, label, color, suffix = "" }) {
  const latest = data.length ? data[data.length - 1][dataKey] : null;
  const display = (latest === null || latest === undefined || Number.isNaN(latest)) ? "—" : `${latest}${suffix}`;
  return (
    <div className="bg-warm-white border border-edge rounded-2xl p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold">{label}</div>
        <div className="font-display text-lg font-bold text-navy">{display}</div>
      </div>
      <div className="mt-2 h-16">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 5, left: -30, bottom: 0 }}>
            <CartesianGrid stroke="#F2ECE6" vertical={false} />
            <XAxis dataKey="s" tick={{ fill: "#7A7A9A", fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
            <YAxis domain={[0, 100]} tick={false} axisLine={false} tickLine={false} width={0} />
            <Tooltip contentStyle={{ background: "#FFFEF9", border: "1px solid #E0D8E8", borderRadius: 10, fontSize: 12 }} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 3, fill: color }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function SymptomRow({ symptomKey, name, checkins, testid }) {
  const rows = checkins
    .filter((c) => !c.is_draft)
    .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
    .map((c) => ({ s: c.session_number, rec: (c.symptom_updates || {})[symptomKey] }))
    .filter((r) => r.rec && r.rec.rating);
  return (
    <div className="flex items-center gap-2 py-1.5" data-testid={testid}>
      <div className="text-xs text-navy font-semibold truncate flex-1 min-w-0">{name}</div>
      {rows.length === 0 ? (
        <div className="text-[10px] text-ink-soft italic">no updates yet</div>
      ) : (
        <div className="flex gap-1 shrink-0">
          {rows.map((r, i) => (
            <div
              key={i}
              title={`Session ${r.s} · ${SYMPTOM_RATING_LABEL[r.rec.rating] || r.rec.rating}${r.rec.detail ? ` — ${r.rec.detail}` : ""}`}
              className="w-6 h-6 rounded-md flex items-center justify-center text-[9px] font-bold text-warm-white"
              style={{ background: SYMPTOM_RATING_COLOR[r.rec.rating] || "#8B5E7A" }}
            >
              S{r.s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChildColumn({ child, checkins, streak, roadblocks }) {
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;
  const num = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);
  const chartData = checkins.filter((c) => !c.is_draft).sort((a, b) => (a.session_number || 0) - (b.session_number || 0)).map((c) => {
    const b = c.breathing || {};
    const l = c.lips || {};
    const t = c.tongue || {};
    const s = c.swallow || {};
    return {
      s: `S${c.session_number}`,
      nasal: Math.round((num(b.nasal_day_pct) + num(b.nasal_night_pct)) / 2),
      lipseal: num(l.sealed_pct),
      tongue: Math.round((num(t.tip_spot_pct) + num(t.mid_palate_pct) + num(t.back_palate_pct)) / 3),
      swallow: s.proper_easy === "yes" ? 100 : s.proper_easy === "somewhat" ? 50 : 0,
    };
  });
  const dismissed = child.dismissed_symptoms || [];
  const trackedSymptoms = (child.top_symptoms || []).filter((k) => !dismissed.includes(k));
  const visibleRoadblocks = roadblocks.filter((r) => r.kind && ROADBLOCK_LABEL[r.kind]);
  return (
    <div className="flex-1 min-w-[300px] bg-cream/40 border border-edge rounded-3xl p-5 md:p-6 space-y-4" data-testid={`compare-column-${child.id}`}>
      <div className="pb-3 border-b" style={{ borderColor: tribe.accent + "55" }}>
        {(child.is_self || child.tribe !== "neutral") && (
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>
            {child.is_self ? "Self-enrolled" : tribe.name}
          </div>
        )}
        <div className="flex items-baseline justify-between gap-3 mt-1">
          <h2 className="font-display text-2xl font-bold text-navy truncate" data-testid={`compare-name-${child.id}`}>{child.name}</h2>
          <Link to={`/child/${child.id}`} data-testid={`compare-open-${child.id}`} className="inline-flex items-center gap-1 text-xs text-mauve font-semibold hover:text-navy shrink-0">
            Open <ArrowRight size={12} />
          </Link>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warm-white border border-edge text-xs">
            <span className="text-ink-soft uppercase tracking-widest text-[10px] font-bold">Session</span>
            <span className="text-navy font-bold">{child.current_session}/12</span>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warm-white border border-edge text-xs" data-testid={`compare-streak-${child.id}`}>
            <Flame size={11} className="text-mauve" />
            <span className="text-navy font-bold">{streak.current}</span>
            <span className="text-ink-soft text-[10px]">day{streak.current === 1 ? "" : "s"}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warm-white border border-edge text-xs">
            <Sparkles size={11} className="text-gold" />
            <span className="text-navy font-bold">{streak.total}</span>
            <span className="text-ink-soft text-[10px]">total</span>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-mauve font-bold mb-2">Progress</div>
        <div className="grid grid-cols-2 gap-2">
          <MiniChart data={chartData} dataKey="nasal" label="Nasal" color="#2C2D5B" suffix="%" />
          <MiniChart data={chartData} dataKey="lipseal" label="Lip Seal" color="#C9A96E" suffix="%" />
          <MiniChart data={chartData} dataKey="tongue" label="Tongue" color="#8B5E7A" suffix="%" />
          <MiniChart data={chartData} dataKey="swallow" label="Swallow" color="#1a1b3a" />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-mauve font-bold mb-2">Symptom timeline</div>
        {trackedSymptoms.length === 0 ? (
          <div className="text-xs text-ink-soft italic">No active symptoms tracked.</div>
        ) : (
          <div className="bg-warm-white border border-edge rounded-2xl px-3 py-1.5 divide-y divide-edge">
            {trackedSymptoms.map((k) => {
              const opt = SYMPTOM_OPTIONS.find((s) => s.key === k);
              return <SymptomRow key={k} symptomKey={k} name={opt ? opt.name : k} checkins={checkins} testid={`compare-symptom-${child.id}-${k}`} />;
            })}
          </div>
        )}
        {dismissed.length > 0 && (
          <div className="mt-2 text-[10px] text-ink-soft" data-testid={`compare-dismissed-${child.id}`}>
            <Sparkles size={9} className="inline text-gold mr-1" />
            Resolved: {dismissed.map((k) => (SYMPTOM_OPTIONS.find((s) => s.key === k)?.name || k)).join(", ")}
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-mauve font-bold mb-2">Roadblocks</div>
        {visibleRoadblocks.length === 0 ? (
          <div className="text-xs text-ink-soft italic">Nothing flagged.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {visibleRoadblocks.map((r) => {
              const style = ROADBLOCK_STATUS_STYLE[r.status] || ROADBLOCK_STATUS_STYLE.none;
              return (
                <div key={r.kind} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${style.bg} ${style.ink}`} data-testid={`compare-roadblock-${child.id}-${r.kind}`}>
                  <Flag size={10} />
                  <span>{ROADBLOCK_LABEL[r.kind]}</span>
                  <span className="opacity-60">·</span>
                  <span>{style.label}</span>
                  {r.kind === "tongue_tie" && r.tongue_tie_streak > 0 && (
                    <span className="ml-0.5 opacity-70">({r.tongue_tie_streak}/3)</span>
                  )}
                  {r.kind === "tonsils_adenoids" && typeof r.severity === "number" && (
                    <span className="ml-0.5 opacity-70">({r.severity}/4)</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FamilyCompare() {
  const [children, setChildren] = useState([]);
  const [data, setData] = useState({}); // childId → { checkins, streak, roadblocks }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const kids = (await api.get("/children")).data;
        setChildren(kids);
        if (kids.length === 0) { setLoading(false); return; }
        const bundles = await Promise.all(kids.map(async (c) => {
          const [ck, st, rb] = await Promise.all([
            api.get(`/checkins/${c.id}`).then((r) => r.data).catch(() => []),
            api.get(`/streak/${c.id}`).then((r) => r.data).catch(() => ({ current: 0, longest: 0, total: 0 })),
            api.get(`/roadblocks/${c.id}`).then((r) => r.data).catch(() => []),
          ]);
          return [c.id, { checkins: ck, streak: st, roadblocks: rb }];
        }));
        setData(Object.fromEntries(bundles));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;

  if (children.length < 2) {
    return (
      <Shell>
        <div className="max-w-2xl mx-auto text-center py-16" data-testid="compare-empty-state">
          <h1 className="font-display text-3xl font-bold text-navy">Compare view opens with two</h1>
          <p className="text-ink-mid mt-3">Enroll a second family member and this screen lines them up side-by-side.</p>
          <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 text-mauve font-semibold">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Family compare</div>
          <h1 className="font-display text-3xl font-bold text-navy mt-1">Side by side</h1>
          <p className="text-ink-mid mt-1 text-sm">Every child's progress charts, symptoms, and roadblocks — one screen, no clicking around.</p>
        </div>
        <Link to="/dashboard" data-testid="compare-back-btn" className="inline-flex items-center gap-2 text-sm text-mauve font-semibold hover:text-navy">
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </div>

      <div className="flex flex-wrap gap-4 items-stretch" data-testid="family-compare-grid">
        {children.map((c) => (
          <ChildColumn
            key={c.id}
            child={c}
            checkins={data[c.id]?.checkins || []}
            streak={data[c.id]?.streak || { current: 0, longest: 0, total: 0 }}
            roadblocks={data[c.id]?.roadblocks || []}
          />
        ))}
      </div>
    </Shell>
  );
}
